import type { AuthClient } from '@auth/client';
import type { ApiErrorResponse } from '@/types/api';

/**
 * Admin API クライアントエラー
 *
 * Admin API から返されるエラー（HTTP エラーを含む）をラップするカスタムエラークラス。
 */
export class AdminApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'AdminApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * Admin API クライアントインターフェース
 */
export interface AdminApiClient {
  get<T>(path: string, params?: Record<string, string>): Promise<T>;
  post<T>(path: string, body: unknown): Promise<T>;
  put<T>(path: string, body: unknown): Promise<T>;
  /**
   * 本文のある応答（例: `{ data: { id, cachePurged } }`）を返す DELETE では T を指定する。
   * 204 No Content なら undefined を返す。
   */
  delete<T = void>(path: string): Promise<T>;
  /**
   * バイナリ（画像など）を本文にして POST する。
   * Blob のバイト列を ArrayBuffer にして渡すので、Content-Length がつく。
   */
  postBinary<T>(path: string, body: Blob, contentType: string): Promise<T>;
}

/** 送る本文。JSON にするものと、そのまま送るバイナリを分ける */
type RequestBody =
  | { readonly kind: 'json'; readonly value: unknown }
  | { readonly kind: 'binary'; readonly value: ArrayBuffer; readonly contentType: string };

const ADMIN_BASE_PATH = '/api/v1/admin';

/**
 * API ベース URL。
 * 本番・ステージングでは VITE_API_BASE_URL（例: https://api.cms.dassou-jyoshuhan-rem.com）、
 * ローカル開発では window.location.origin（Vite proxy で /admin → backend に転送）。
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || window.location.origin;

/** Admin API へのリクエスト URL を構築する */
function buildUrl(path: string, params?: Record<string, string>): string {
  const url = new URL(`${ADMIN_BASE_PATH}${path}`, API_BASE_URL);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, value);
    }
  }
  return url.toString();
}

/**
 * エラーレスポンスをパースして AdminApiError をスローする。
 *
 * バックエンドの統一エラー形式 `{ error, code, details? }` を優先してパースし、
 * パース不能な場合は汎用エラーとして処理する。
 */
async function parseErrorResponse(response: Response): Promise<never> {
  let body: unknown;
  try {
    const text = await response.text();
    body = JSON.parse(text);
  } catch {
    throw new AdminApiError(
      response.status,
      'NETWORK_ERROR',
      `サーバーエラーが発生しました (${response.status})`,
    );
  }

  if (
    body !== null &&
    typeof body === 'object' &&
    'error' in body &&
    typeof (body as Record<string, unknown>).error === 'string'
  ) {
    const parsed = body as ApiErrorResponse;
    throw new AdminApiError(response.status, parsed.code, parsed.error, parsed.details);
  }

  throw new AdminApiError(
    response.status,
    'UNKNOWN',
    `サーバーエラーが発生しました (${response.status})`,
  );
}

/** fetch に渡す本文を作る。バイナリは ArrayBuffer のまま渡す（401 の送り直しでも同じものを使える） */
function toFetchBody(body: RequestBody | undefined): BodyInit | undefined {
  if (body === undefined) return undefined;
  if (body.kind === 'binary') return body.value;
  return body.value !== undefined ? JSON.stringify(body.value) : undefined;
}

/**
 * Admin API クライアントを生成するファクトリ関数
 *
 * @auth/client の AuthClient と統合し、以下の機能を提供する:
 * - Bearer トークンの自動付与
 * - 統一的なエラーレスポンスパース（4xx/5xx）
 * - 401 時のトークン自動リフレッシュ（1回のみ試行）
 * - リフレッシュ失敗時の自動ログアウト
 *
 * @param authClient - @auth/client の AuthClient インスタンス
 */
export function createAdminApiClient(authClient: AuthClient): AdminApiClient {
  async function request<T>(
    method: string,
    path: string,
    body?: RequestBody,
    params?: Record<string, string>,
    retried = false,
  ): Promise<T> {
    const token = authClient.getAccessToken();
    const url = buildUrl(path, params);

    const headers: Record<string, string> = {
      'Content-Type': body?.kind === 'binary' ? body.contentType : 'application/json',
    };
    if (token !== null) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method,
      headers,
      body: toFetchBody(body),
    });

    // 401: トークンリフレッシュを試みる（1回のみ）
    if (response.status === 401) {
      if (retried) {
        // リフレッシュ後も 401 → ログアウトしてエラーをスロー
        await authClient.logout();
        throw new AdminApiError(
          401,
          'AUTH_001',
          'セッションが期限切れです。再ログインしてください。',
        );
      }

      // リフレッシュトークンが無い場合はリフレッシュ試行をスキップし、即座にログアウト
      const refreshToken = authClient.getRefreshToken();
      if (refreshToken === null) {
        await authClient.logout();
        throw new AdminApiError(401, 'SESSION_EXPIRED', 'セッションが期限切れです。');
      }

      try {
        await authClient.refreshToken();
        return request<T>(method, path, body, params, true);
      } catch {
        await authClient.logout();
        throw new AdminApiError(
          401,
          'AUTH_001',
          'セッションが期限切れです。再ログインしてください。',
        );
      }
    }

    if (!response.ok) {
      return parseErrorResponse(response);
    }

    // 204 No Content（DELETE 等）
    if (response.status === 204) {
      return undefined as T;
    }

    const data = (await response.json()) as T;
    return data;
  }

  return {
    get<T>(path: string, params?: Record<string, string>): Promise<T> {
      return request<T>('GET', path, undefined, params);
    },
    post<T>(path: string, body: unknown): Promise<T> {
      return request<T>('POST', path, { kind: 'json', value: body });
    },
    put<T>(path: string, body: unknown): Promise<T> {
      return request<T>('PUT', path, { kind: 'json', value: body });
    },
    delete<T = void>(path: string): Promise<T> {
      return request<T>('DELETE', path);
    },
    async postBinary<T>(path: string, body: Blob, contentType: string): Promise<T> {
      // Blob を ArrayBuffer にしてから送る。jsdom など Blob の実装が fetch と違う環境でも
      // 中身がそのまま送られ、ブラウザでも長さの決まった本文として Content-Length がつく。
      const bytes = await body.arrayBuffer();
      return request<T>('POST', path, { kind: 'binary', value: bytes, contentType });
    },
  };
}
